'use client';

import Image from 'next/image';
import Link from '~/(tenant)/_components/tenant-link';
import { ChevronDown } from 'lucide-react';

import { publicStorageUrl } from '@kit/shared/storage';
import { currencyMinorUnits, formatCurrency } from '@kit/shared/currency';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { slugifyCoachName } from '~/(tenant)/coaches/_lib/coach-slug';

import type { AboutBlock, CoachesBlock, CtaBlock, PackagesBlock, ServicesBlock } from '~/(tenant)/_lib/schema/landing-config.schema';

import { type LandingRenderContext, landingImageUrl, pickLocalized } from './landing-context';
import styles from './studio-blocks.module.css';
import { SmartLink } from './smart-link';

export function PhotoBandCta({ block, ctx }: { block: CtaBlock; ctx: LandingRenderContext }) {
  const label = pickLocalized(block.ctaLabel, block.ctaLabel_ar, ctx.locale)?.trim();
  const secondaryLabel = pickLocalized(block.secondaryCtaLabel, block.secondaryCtaLabel_ar, ctx.locale)?.trim();
  return <section className={styles.photoBand} data-tone={block.style ?? 'primary'} style={block.imageKey ? { backgroundImage: `linear-gradient(color-mix(in srgb, var(--photo-band-bg) 75%, transparent), color-mix(in srgb, var(--photo-band-bg) 75%, transparent)), url("${landingImageUrl(ctx, block.imageKey)}")` } : undefined}>
    {block.eyebrow ? <p className="landing-eyebrow">{pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale)}</p> : null}
    <h2>{pickLocalized(block.headline, block.headline_ar, ctx.locale)}</h2>
    <p>{pickLocalized(block.body, block.body_ar, ctx.locale)}</p>
    {block.ctaHref && label ? <SmartLink href={block.ctaHref} className="landing-button">{label}</SmartLink> : null}
    {block.secondaryCtaHref && secondaryLabel ? <SmartLink href={block.secondaryCtaHref} className="landing-button">{secondaryLabel}</SmartLink> : null}
  </section>;
}

export function CenteredAbout({ block, ctx }: { block: AboutBlock; ctx: LandingRenderContext }) {
  const label = pickLocalized(block.ctaLabel, block.ctaLabel_ar, ctx.locale)?.trim();
  return <section className={styles.about}>
    {block.imageKey ? <Image src={landingImageUrl(ctx, block.imageKey)} alt="" width={240} height={320} unoptimized className={styles.mark} /> : null}
    <h2>{pickLocalized(block.title, block.title_ar, ctx.locale)}</h2>
    <p className="landing-prose">{pickLocalized(block.body, block.body_ar, ctx.locale)}</p>
    {block.ctaHref && label ? <SmartLink href={block.ctaHref}>{label}</SmartLink> : null}
  </section>;
}

export function StudioServices({ block, ctx }: { block: ServicesBlock; ctx: LandingRenderContext }) {
  const cards: NonNullable<ServicesBlock['cards']> = block.mode === 'manual' ? block.cards ?? [] : ctx.services.map((service) => ({ title: service.name, description: service.description }));
  return <section className={block.layout === 'steps' ? styles.steps : styles.info}>
    {block.title ? <h2>{pickLocalized(block.title, block.title_ar, ctx.locale)}</h2> : null}
    <div className={styles.columns}>
      {cards.map((card, index) => <article key={index}>
        {card.imageKey ? <Image src={landingImageUrl(ctx, card.imageKey)} alt="" width={240} height={320} unoptimized className={styles.mark} /> : null}
        {card.eyebrow ? <p className={styles.stepLabel}>{pickLocalized(card.eyebrow, card.eyebrow_ar, ctx.locale)}</p> : null}
        <h3>{pickLocalized(card.title, card.title_ar, ctx.locale)}</h3>
        <p className="landing-prose">{pickLocalized(card.description, card.description_ar, ctx.locale)}</p>
        {card.href && pickLocalized(card.ctaLabel, card.ctaLabel_ar, ctx.locale)?.trim() ? <SmartLink href={card.href}>{pickLocalized(card.ctaLabel, card.ctaLabel_ar, ctx.locale)?.trim()}</SmartLink> : null}
      </article>)}
    </div>
  </section>;
}

export function PortraitCoaches({ block, ctx }: { block: CoachesBlock; ctx: LandingRenderContext }) {
  const arabic = ctx.locale.startsWith('ar');
  if (!ctx.coaches.length && !block.showPlaceholders) return null;
  return <section className={styles.coaches} data-test="portrait-coaches">
    <h2>{pickLocalized(block.title, block.title_ar, ctx.locale) ?? (arabic ? 'تعرّفي على المدربات' : 'Meet your instructors')}</h2>
    <div className={styles.portraits}>
      {ctx.coaches.length ? ctx.coaches.map((coach) => <article key={coach.id}>
        {coach.pictureUrl ? <Image className={styles.portrait} src={publicStorageUrl(coach.pictureUrl)} alt={coach.name} width={900} height={1200} unoptimized /> : <div className={styles.portrait} aria-label={arabic ? 'صورة المدربة غير متاحة' : 'Instructor photograph unavailable'} />}
        <h3>{coach.name}</h3>
        <p>{arabic ? 'مدربة' : 'Instructor'}</p>
        <Link href={`/coaches/${slugifyCoachName(coach.name, coach.id)}`}>{arabic ? 'اقرئي السيرة' : 'Read Bio'}</Link>
      </article>) : Array.from({ length: 3 }, (_, index) => <article key={index} data-placeholder>
        <div className={styles.portrait} aria-hidden="true" />
        <h3>{arabic ? 'تفاصيل المدربة قريباً' : 'Instructor details coming soon'}</h3>
        <p>{arabic ? 'لم تُعلن المدربات بعد' : 'Instructors have not been announced yet'}</p>
      </article>)}
    </div>
  </section>;
}

export function AccordionPackages({ block, ctx }: { block: PackagesBlock; ctx: LandingRenderContext }) {
  const currency = useTenantCurrency();
  const arabic = ctx.locale.startsWith('ar');
  const packages = ctx.packages.slice(0, block.limit ?? 4);
  if (!packages.length && !block.showPlaceholders) return null;
  return <section className={styles.packages} data-test="accordion-packages">
    {block.imageKey ? <Image src={landingImageUrl(ctx, block.imageKey)} alt="" width={1200} height={1600} unoptimized className={styles.packagePhoto} /> : null}
    <div className={styles.packageContent}>
      {block.eyebrow ? <p className="landing-eyebrow">{pickLocalized(block.eyebrow, block.eyebrow_ar, ctx.locale)}</p> : null}
      <h2>{pickLocalized(block.title, block.title_ar, ctx.locale) ?? (arabic ? 'باقات الاستوديو' : 'Studio packages')}</h2>
      <p className="landing-prose">{pickLocalized(block.subtitle, block.subtitle_ar, ctx.locale)}</p>
      <div className={styles.accordions}>
        {packages.length ? packages.map((pkg) => <details key={pkg.id}>
          <summary>{pkg.name}<ChevronDown size={16} aria-hidden="true" /></summary>
          <div>
            <p className={styles.price}><CurrencyIcon className="h-4 w-4" /><span>{formatCurrency(pkg.price, currency, ctx.locale, { style: 'decimal', minimumFractionDigits: currencyMinorUnits(currency), maximumFractionDigits: currencyMinorUnits(currency) })}</span></p>
            <p>{pickLocalized(pkg.description ?? undefined, pkg.descriptionAlt ?? undefined, ctx.locale)}</p>
            <ul>
              <li>{arabic ? `${pkg.bookingCount} حصص` : `${pkg.bookingCount} sessions`}</li>
              <li>{pkg.validityDays ? (arabic ? `صالحة ${pkg.validityDays} يوماً` : `Valid ${pkg.validityDays} days`) : (arabic ? `صالحة ${pkg.validityMonths} أشهر` : `Valid ${pkg.validityMonths} months`)}</li>
              {(arabic && pkg.featuresAlt.length ? pkg.featuresAlt : pkg.features).map((feature, index) => <li key={index}>{feature}</li>)}
            </ul>
            <Link href={`/packages?package=${encodeURIComponent(pkg.id)}`}>{arabic ? 'عرض الباقة' : 'View package'}</Link>
          </div>
        </details>) : Array.from({ length: 4 }, (_, index) => <details key={index} data-placeholder>
          <summary>{arabic ? 'تفاصيل الباقة قريباً' : 'Package details coming soon'}<ChevronDown size={16} aria-hidden="true" /></summary>
          <div><p>{arabic ? 'لا توجد باقات متاحة للشراء حالياً. ستظهر الباقات المنشورة هنا.' : 'No packages are available to purchase yet. Published packages will appear here.'}</p></div>
        </details>)}
      </div>
    </div>
  </section>;
}
